/**
 * Image optimization utilities for resizing images before upload
 * Uses native Canvas API for zero dependencies
 *
 * NOTE: This is browser-only. For React Native, use:
 * - react-native-image-resizer
 * - expo-image-manipulator
 */

/**
 * Check if a file is HEIC/HEIF format (common on iPhones)
 */
export function isHeicFile(file: File): boolean {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  return (
    type === 'image/heic' ||
    type === 'image/heif' ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  );
}

/**
 * Convert HEIC/HEIF image to JPEG for browser compatibility
 * Lazy-loads heic2any library only when needed (~500KB)
 */
export async function convertHeicToJpeg(file: File): Promise<File> {
  // Dynamic import to avoid bundling heic2any in main chunk
  const heic2any = (await import('heic2any')).default;

  const blob = await heic2any({
    blob: file,
    toType: 'image/jpeg',
    quality: 0.92,
  });

  // heic2any can return a single blob or array of blobs
  const resultBlob = Array.isArray(blob) ? blob[0] : blob;
  const newName = file.name.replace(/\.(heic|heif)$/i, '.jpg');

  return new File([resultBlob], newName, { type: 'image/jpeg' });
}

export interface ResizeOptions {
  /** Maximum width or height in pixels */
  maxSize: number;
  /** Output quality for JPEG/WebP (0-1), default 0.85 */
  quality?: number;
  /** Output format, default 'image/webp' for best compression */
  format?: 'image/webp' | 'image/jpeg' | 'image/png';
}

/** Default resize options by image type */
export const RESIZE_PRESETS: Record<
  'profile' | 'group' | 'expense',
  ResizeOptions
> = {
  profile: { maxSize: 256, quality: 0.85, format: 'image/webp' },
  group: { maxSize: 1200, quality: 0.85, format: 'image/webp' },
  // Expense receipts use small thumbnails (256px) for list views,
  // full original is stored separately and shown when clicked
  expense: { maxSize: 256, quality: 0.85, format: 'image/webp' },
};

/**
 * Resize an image file while maintaining aspect ratio
 * Returns the resized file and a data URL for preview
 */
export async function resizeImage(
  file: File,
  options: ResizeOptions
): Promise<{ file: File; dataUrl: string }> {
  const { maxSize, quality = 0.85, format = 'image/webp' } = options;

  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = () => {
      URL.revokeObjectURL(img.src);

      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img;

      if (width > maxSize || height > maxSize) {
        if (width > height) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Use high-quality image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw the resized image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to blob
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Could not create image blob'));
            return;
          }

          // Determine file extension from format
          const ext =
            format === 'image/webp'
              ? 'webp'
              : format === 'image/jpeg'
                ? 'jpg'
                : 'png';
          const fileName = file.name.replace(/\.[^.]+$/, `.${ext}`);

          const resizedFile = new File([blob], fileName, { type: format });
          const dataUrl = canvas.toDataURL(format, quality);

          resolve({ file: resizedFile, dataUrl });
        },
        format,
        quality
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Could not load image'));
    };

    // Load the image from the file
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Re-encode an image as a JPEG, scaling it down only when its long side is
 * over `maxLongSide`. Always re-encodes, so PNG, WebP and decoded HEIC all
 * come out as JPEG.
 */
export async function encodeJpegLongSide(
  source: Blob,
  maxLongSide: number,
  quality: number
): Promise<{ blob: Blob; width: number; height: number }> {
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    const url = URL.createObjectURL(source);
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not load image'));
    };
    image.src = url;
  });

  const scale = Math.min(1, maxLongSide / Math.max(img.width, img.height));
  const width = Math.round(img.width * scale);
  const height = Math.round(img.height * scale);

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  // JPEG has no alpha: without a fill, a transparent screenshot turns black.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      result =>
        result ? resolve(result) : reject(new Error('Could not encode image')),
      'image/jpeg',
      quality
    );
  });
  return { blob, width, height };
}

/**
 * Crop area as returned by react-easy-crop
 */
export interface CropArea {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Crop an image using Canvas API
 * Takes a data URL and crop coordinates, returns a cropped File
 */
export async function cropImage(
  imageSrc: string,
  cropArea: CropArea,
  fileName: string = 'cropped.jpg'
): Promise<File> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Could not get canvas context'));
      return;
    }

    img.onload = () => {
      // Set canvas size to crop dimensions
      canvas.width = cropArea.width;
      canvas.height = cropArea.height;

      // Use high-quality image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      // Draw the cropped portion of the image
      ctx.drawImage(
        img,
        cropArea.x,
        cropArea.y,
        cropArea.width,
        cropArea.height,
        0,
        0,
        cropArea.width,
        cropArea.height
      );

      // Convert to blob (JPEG for best compatibility)
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error('Could not create image blob'));
            return;
          }

          const croppedFile = new File([blob], fileName, {
            type: 'image/jpeg',
          });
          resolve(croppedFile);
        },
        'image/jpeg',
        0.92
      );
    };

    img.onerror = () => {
      reject(new Error('Could not load image for cropping'));
    };

    img.src = imageSrc;
  });
}

/**
 * Check if an image needs resizing based on its dimensions
 */
export async function getImageDimensions(
  file: File
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(img.src);
      resolve({ width: img.width, height: img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(img.src);
      reject(new Error('Could not load image'));
    };
    img.src = URL.createObjectURL(file);
  });
}

/**
 * Process an image for upload - creates optimized version for display
 * Handles HEIC/HEIF conversion for iPhone photos
 * Returns both optimized and original files for dual upload
 */
export async function optimizeImageForUpload(
  file: File,
  type: 'profile' | 'group' | 'expense'
): Promise<{
  optimized: File;
  original: File;
  preview: string;
}> {
  // Convert HEIC/HEIF to JPEG first (iPhone photos)
  let processedFile = file;
  if (isHeicFile(file)) {
    processedFile = await convertHeicToJpeg(file);
  }

  const preset = RESIZE_PRESETS[type];

  // Create optimized version
  const { file: optimized, dataUrl } = await resizeImage(processedFile, preset);

  return {
    optimized,
    original: processedFile, // Use converted file as original (JPEG instead of HEIC)
    preview: dataUrl,
  };
}
